/**
 * iter92 — FactureAEmettre
 *
 * Page d'édition d'une "facture à émettre" (OR au statut 'pret', sans facture émise).
 * Accessible depuis Facturation → onglet "À émettre".
 *
 * Permet AVANT émission :
 *   - d'éditer les lignes / articles / prix / rabais (modifie l'OR — source unique)
 *   - d&apos;éditer l&apos;en-tête et le pied de page (via LignesEditor partagé)
 *   - de compléter les infos client (→ sauvegardées sur la fiche client en DB)
 *   - de compléter les infos véhicule (→ sauvegardées sur la fiche véhicule en DB)
 * Puis d'émettre via : Caisse / Mail / Lixt / Courrier / Hybride (EmissionActions).
 */
import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  ArrowLeft, Loader2, FileText, User, Car as CarIcon, CheckCircle2, Wrench,
  ShieldCheck, Search, X,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import useOrDraft from "@/hooks/useOrDraft";
import InlineEditField from "@/components/InlineEditField";
import VehiculeCard from "@/components/VehiculeCard";
import IntervenantsEditor from "@/components/IntervenantsEditor";
import LignesEditor from "@/components/LignesEditor";
import DocumentHeader from "@/components/DocumentHeader";
import ExcelImportDialog from "@/components/ExcelImportDialog";
import {
  ArticlePickerDialog, ImportDevisDialog,
} from "@/components/or";
import EmissionActions from "@/components/facturation/EmissionActions";
import OrReferencesBlock from "@/components/or/OrReferencesBlock";
import {
  useUnsavedChangesGuard, UnsavedChangesBanner,
} from "@/components/UnsavedChangesGuard";
import { openAuthBlob } from "@/lib/downloadPdf";
import { buildLigneFromArticle } from "@/lib/articleToLigne";

function ClientEditCard({ client, onSaved }) {
  if (!client) return null;
  const save = async (field, value) => {
    await api.patch(`/clients/${client.id}`, { [field]: value });
    onSaved?.();
  };
  const isPro = client.type === "entreprise" || !!client.raison_sociale;
  const fields = [
    ...(isPro
      ? [["Raison sociale", "raison_sociale"]]
      : [["Prénom", "prenom"], ["Nom", "nom"]]),
    ["Adresse", "adresse"],
    ["NPA", "npa"],
    ["Localité", "localite"],
    ["Téléphone", "tel_principal", "tel"],
    ["Email", "email", "email"],
  ];
  return (
    <Card className="p-4 border-gray-200" data-testid="fae-client-card">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold flex items-center gap-1.5">
          <User className="w-3.5 h-3.5" /> Client — modifiable (sauvé sur la fiche)
        </div>
      </div>
      <div className="grid grid-cols-2 gap-x-3 gap-y-2.5 text-xs">
        {fields.map(([label, field, type]) => (
          <div key={field} className={field === "adresse" ? "col-span-2" : "min-w-0"}>
            <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-500">{label}</div>
            <div className="text-sm text-gray-900 mt-0.5">
              <InlineEditField
                value={client[field]}
                onSave={(v) => save(field, v)}
                type={type || "text"}
                placeholder={`Ajouter ${label.toLowerCase()}…`}
                testId={`fae-client-${field}`}
                inputClassName="w-full text-sm"
              />
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

/**
 * Task #3 — Carte "Assurance liée" : permet de lier/délier une compagnie
 * d'assurance à l'OR via `assurance_id`. Contrairement au "Destinataire"
 * (EmissionActions → réattache l'OR à un autre client), ceci NE MODIFIE JAMAIS
 * le client/véhicule réel de l'OR : seule la facturation (raison_sociale,
 * adresse, IDE, email) sera adressée à l'assurance dès qu'elle est liée,
 * quel que soit le canal d'émission choisi ensuite (Caisse/Mail/Lixt/Courrier).
 */
function AssuranceLinkCard({ or, onUpdated }) {
  const [picker, setPicker] = useState({ open: false, q: "", results: [], loading: false });
  const [busy, setBusy] = useState(false);
  const assurance = or?.assurance_detail;

  useEffect(() => {
    if (!picker.open) return;
    const q = (picker.q || "").trim();
    if (q.length < 2) { setPicker((p) => ({ ...p, results: [], loading: false })); return; }
    let alive = true;
    setPicker((p) => ({ ...p, loading: true }));
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/clients", { params: { q, type: "assurance", limit: 15 } });
        if (!alive) return;
        setPicker((p) => ({ ...p, results: data?.items || data || [], loading: false }));
      } catch {
        if (alive) setPicker((p) => ({ ...p, loading: false }));
      }
    }, 250);
    return () => { alive = false; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [picker.q, picker.open]);

  const link = async (assuranceId) => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/or/${or.id}`, { assurance_id: assuranceId });
      onUpdated?.(data);
      setPicker({ open: false, q: "", results: [], loading: false });
      toast.success(assuranceId ? "Assurance liée à l'OR" : "Assurance retirée");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="p-4 border-violet-200 bg-violet-50/40" data-testid="fae-assurance-card">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] uppercase tracking-wider text-violet-700 font-semibold flex items-center gap-1.5">
          <ShieldCheck className="w-3.5 h-3.5" /> Assurance liée (destinataire de la facture)
        </div>
        {assurance && (
          <Button size="sm" variant="ghost" disabled={busy} onClick={() => link(null)} data-testid="fae-assurance-remove">
            <X className="w-3.5 h-3.5 mr-1" /> Retirer
          </Button>
        )}
      </div>
      {assurance ? (
        <div className="text-sm">
          <div className="font-semibold text-gray-900">{assurance.raison_sociale}</div>
          <div className="text-xs text-gray-600 mt-0.5">
            {assurance.adresse}, {assurance.npa} {assurance.localite}
            {assurance.email && ` · ${assurance.email}`}
          </div>
          <div className="text-[11px] text-violet-700 mt-1.5">
            La facture sera adressée à cette assurance. Le client/véhicule de l&apos;OR ne changent pas.
          </div>
        </div>
      ) : (
        <Button
          size="sm" variant="outline"
          onClick={() => setPicker({ open: true, q: "", results: [], loading: false })}
          className="border-violet-300 text-violet-700 hover:bg-violet-100"
          data-testid="fae-assurance-link-btn"
        >
          <ShieldCheck className="w-3.5 h-3.5 mr-1.5" /> Lier une assurance
        </Button>
      )}

      <Dialog open={picker.open} onOpenChange={(o) => !o && setPicker({ open: false, q: "", results: [], loading: false })}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Lier une assurance</DialogTitle>
            <DialogDescription>
              La facture de cet OR sera adressée à l&apos;assurance choisie, sans modifier le client/véhicule réel.
            </DialogDescription>
          </DialogHeader>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input
              value={picker.q}
              onChange={(e) => setPicker((p) => ({ ...p, q: e.target.value }))}
              placeholder="Rechercher une assurance (Generali, AXA, Zurich...)"
              className="pl-9"
              autoFocus
              data-testid="fae-assurance-search"
            />
          </div>
          <div className="max-h-72 overflow-y-auto border rounded-lg divide-y divide-gray-100">
            {picker.loading && (
              <div className="p-3 text-center text-sm text-gray-500">
                <Loader2 className="w-4 h-4 animate-spin inline mr-1.5" /> Recherche...
              </div>
            )}
            {!picker.loading && picker.q.trim().length < 2 && (
              <div className="p-4 text-center text-xs text-gray-400">Tape au moins 2 caractères pour rechercher.</div>
            )}
            {!picker.loading && picker.q.trim().length >= 2 && picker.results.length === 0 && (
              <div className="p-4 text-center text-xs text-gray-500">Aucune assurance trouvée.</div>
            )}
            {picker.results.map((c) => (
              <button
                type="button"
                key={c.id}
                onClick={() => link(c.id)}
                disabled={busy}
                className="w-full text-left p-3 hover:bg-violet-50 transition-colors"
                data-testid={`fae-assurance-option-${c.id}`}
              >
                <div className="font-semibold text-sm text-gray-900">{c.raison_sociale}</div>
                <div className="text-[11px] text-gray-500 truncate">
                  {[c.localite, c.email].filter(Boolean).join(" · ")}
                </div>
              </button>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPicker({ open: false, q: "", results: [], loading: false })}>
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

export default function FactureAEmettre() {
  const { orId } = useParams();
  const navigate = useNavigate();
  const {
    or, setOr, draft, setDraft, loading, notFound, saving,
    fetchOr, isDirty, dirtyCount, saveAll, cancelAll, totaux,
  } = useOrDraft(orId);

  const [tauxHoraires, setTauxHoraires] = useState({});
  const [mecaniciens, setMecaniciens] = useState([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [editingLigneIdx, setEditingLigneIdx] = useState(null);
  const [excelImportOpen, setExcelImportOpen] = useState(false);
  const [importDevisOpen, setImportDevisOpen] = useState(false);

  useEffect(() => {
    api.get("/atelier/mecaniciens")
      .then(({ data }) => setMecaniciens(data || []))
      .catch(() => setMecaniciens([]));
  }, []);

  useEffect(() => {
    if (!draft) return;
    api.get("/taux-horaires").then(({ data }) => {
      const map = {};
      data.filter((t) => t.type_tarif === draft.type_client_tarif).forEach((t) => {
        map[t.categorie] = t.taux_ht;
      });
      setTauxHoraires(map);
    });
  }, [draft?.type_client_tarif]);

  useUnsavedChangesGuard(isDirty, saveAll);

  if (loading) {
    return <div className="p-8 text-center text-gray-400"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>;
  }
  if (notFound || !or) {
    return (
      <div className="p-8 text-center text-gray-500">
        OR introuvable.{" "}
        <button className="text-[#3d8a5c] underline" onClick={() => navigate("/facturation")}>
          Retour à la facturation
        </button>
      </div>
    );
  }

  // Garde : OR déjà facturé ou pas en file "À émettre"
  if (or.facture_id || or.statut !== "pret") {
    return (
      <div className="p-4 sm:p-6 lg:p-8 max-w-3xl mx-auto" data-testid="fae-guard">
        <button onClick={() => navigate("/facturation")} className="text-sm text-gray-500 hover:text-gray-900 inline-flex items-center gap-1 mb-4">
          <ArrowLeft className="w-4 h-4" /> Retour à la facturation
        </button>
        <Card className="p-6 border-gray-200 text-center">
          {or.facture_id ? (
            <>
              <CheckCircle2 className="w-10 h-10 mx-auto text-emerald-500 mb-2" />
              <div className="font-semibold text-gray-900">
                La facture {or.facture_numero} a déjà été émise pour l&apos;OR {or.numero}.
              </div>
              <Button
                className="mt-4 bg-[#52a675] hover:bg-[#3d8a5c]"
                onClick={() => navigate(`/facturation/${or.facture_id}`)}
                data-testid="fae-goto-facture"
              >
                Voir la facture
              </Button>
            </>
          ) : (
            <>
              <Wrench className="w-10 h-10 mx-auto text-amber-500 mb-2" />
              <div className="font-semibold text-gray-900">
                L&apos;OR {or.numero} n&apos;est pas dans la file &quot;Factures à émettre&quot; (statut actuel : {or.statut}).
              </div>
              <Button
                className="mt-4"
                variant="outline"
                onClick={() => navigate(`/atelier/or/${or.id}`)}
                data-testid="fae-goto-or"
              >
                Ouvrir l&apos;OR
              </Button>
            </>
          )}
        </Card>
      </div>
    );
  }

  const previewPdf = async () => {
    if (isDirty) {
      const ok = await saveAll();
      if (!ok) return;
    }
    openAuthBlob(`/api/or/${or.id}/facture/pdf`)
      .then(() => fetchOr())
      .catch(() => toast.error("Erreur de génération du PDF"));
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-6xl mx-auto" data-testid="fae-page">
      {isDirty && (
        <UnsavedChangesBanner
          count={dirtyCount}
          onSave={saveAll}
          onCancel={cancelAll}
          saving={saving}
        />
      )}

      <button
        onClick={() => navigate("/facturation")}
        className="text-sm text-gray-500 hover:text-gray-900 inline-flex items-center gap-1 mb-3 mt-2"
        data-testid="fae-back"
      >
        <ArrowLeft className="w-4 h-4" /> Retour à la facturation
      </button>

      {/* Header */}
      <DocumentHeader
        icon={FileText}
        iconBgClass="bg-amber-100"
        iconColorClass="text-amber-700"
        numero={`Facture à émettre`}
        testidNumero="fae-title"
        badges={
          <>
            <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
              OR {or.numero}
            </Badge>
            {or.facture_numero && (
              <Badge variant="outline" className="bg-gray-50 text-gray-600 border-gray-200 font-mono">
                N° réservé : {or.facture_numero}
              </Badge>
            )}
          </>
        }
        totalTtc={totaux?.total_ttc}
        monnaie="CHF"
        testidRoot="fae-header"
      >
        <div className="flex items-center gap-2 flex-wrap mt-1">
          <button
            onClick={() => navigate(`/clients/${or.client_id}`)}
            className="text-sm text-[#3d8a5c] hover:underline inline-flex items-center gap-1"
            data-testid="fae-client-link"
          >
            <User className="w-3.5 h-3.5" /> {or.client_nom}
          </button>
          {or.vehicule_label && (
            <span className="ml-3 text-sm text-gray-700 inline-flex items-center gap-1">
              <CarIcon className="w-3.5 h-3.5 text-[#52a675]" /> {or.vehicule_label}
            </span>
          )}
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={previewPdf}
          className="mt-2 border-[#52a675]/40 text-[#2d5f3f] hover:bg-[#52a675]/5"
          data-testid="fae-preview-pdf"
        >
          <FileText className="w-3.5 h-3.5 mr-1.5" /> Aperçu PDF
        </Button>
      </DocumentHeader>

      {/* Émission — visible en haut pour un accès direct */}
      <div className="mb-4">
        <EmissionActions
          orId={or.id}
          total={totaux?.total_ttc}
          clientEmail={or.client_detail?.email}
          emailOptions={[
            { email: or.assurance_detail?.email_factures, label: "assurance · facturation" },
            { email: or.assurance_detail?.email, label: "assurance" },
            { email: or.client_detail?.email_factures, label: "client · facturation" },
            { email: or.client_detail?.email, label: "client" },
          ].filter((o) => o.email)}
          clientId={or.client_id}
          clientLabel={or.client_detail?.raison_sociale || `${or.client_detail?.prenom || ""} ${or.client_detail?.nom || ""}`.trim() || or.client_nom}
          orTypeClientTarif={or.type_client_tarif}
          orImportMetadata={or.import_metadata}
          orNoSinistre={or.no_sinistre}
          orNumero={or.numero}
          beforeAction={async () => (isDirty ? await saveAll() : true)}
          onEmitted={(res) => {
            if (res?.facture_id) navigate(`/facturation/${res.facture_id}`);
            else fetchOr();
          }}
        />
      </div>

      {/* Assurance liée (Task #3) — destinataire de facturation, sans impact sur le client/véhicule réel */}
      <div className="mb-4">
        <AssuranceLinkCard or={or} onUpdated={setOr} />
        {/* iter233 — Références / en-têtes du dossier (N° sinistre & co) reprises sur la facture */}
        <Card className="p-4 border-gray-200 mt-3">
          <div className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">
            En-têtes / références du dossier
          </div>
          <OrReferencesBlock
            or={or}
            onUpdated={(data) => { setOr(data); setDraft((d) => (d ? { ...d, references: data.references } : d)); }}
          />
        </Card>
      </div>

      {/* Client + Véhicule */}
      <div className="grid lg:grid-cols-2 gap-4 mb-4">
        <ClientEditCard client={or.client_detail} onSaved={fetchOr} />
        <Card className="p-4 border-gray-200" data-testid="fae-vehicule-card">
          <div className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold mb-2 flex items-center gap-1.5">
            <CarIcon className="w-3.5 h-3.5" /> Véhicule — modifiable (sauvé sur la fiche)
          </div>
          {or.vehicule_detail ? (
            <>
              <VehiculeCard vehicule={or.vehicule_detail} onUpdate={fetchOr} />
              <div className="mt-2 text-xs text-gray-600 flex items-center gap-1.5">
                <span className="font-semibold uppercase text-[10px] tracking-wider text-gray-500">Km au dépôt :</span>
                <InlineEditField
                  value={or.km_depose}
                  onSave={async (v) => {
                    const km = v ? parseInt(v, 10) : null;
                    const { data } = await api.patch(`/or/${or.id}`, { km_depose: km });
                    setOr(data);
                    setDraft((d) => (d ? { ...d, km_depose: km } : d));
                  }}
                  type="number"
                  inputMode="numeric"
                  placeholder="ajouter km"
                  testId="fae-km-inline"
                  inputClassName="w-28 text-xs"
                />
              </div>
            </>
          ) : (
            <div className="text-sm text-gray-400 italic">Aucun véhicule lié</div>
          )}
        </Card>
      </div>

      {/* iter127 — Lignes via LignesEditor partagé + Card sœur en-tête/pied/intervenants */}
      <Card className="border-gray-200 p-4">
        <LignesEditor
          lignes={draft?.lignes || []}
          setLignes={(next) => setDraft((d) => ({ ...d, lignes: next }))}
          rabaisGlobal={{ pct: draft?.rabais_global_pct || 0, chf: draft?.rabais_global_chf || 0 }}
          setRabaisGlobal={(rg) => setDraft((d) => ({ ...d, rabais_global_pct: rg.pct, rabais_global_chf: rg.chf }))}
          tvaField="tva_pct"
          tauxHoraires={tauxHoraires}
          externalArticlePicker
          onPickArticleClick={(idx) => { setEditingLigneIdx(idx); setPickerOpen(true); }}
          externalExcelImport
          onExcelImportClick={() => setExcelImportOpen(true)}
          extraToolbarButtons={
            <Button
              type="button" size="sm" variant="outline"
              onClick={() => setImportDevisOpen(true)}
              className="border-amber-300 text-amber-700 hover:bg-amber-50"
              data-testid="add-ligne-import-devis"
              title="Récupérer les lignes d'un devis déjà créé"
            >
              <FileText className="w-3.5 h-3.5 sm:mr-1" />
                  <span className="hidden sm:inline">Importer un devis</span>
            </Button>
          }
        />
      </Card>

      <Card className="border-gray-200 p-4 mt-4">
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs uppercase tracking-wider text-gray-500">
              Texte d&apos;en-tête (PDF)
            </Label>
            <Textarea
              value={draft?.entete_libre || ""}
              onChange={(e) => setDraft((d) => ({ ...d, entete_libre: e.target.value }))}
              placeholder="Texte affiché en haut du devis / facture PDF…"
              rows={3}
              className="text-sm mt-1"
              data-testid="fae-entete-libre"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-gray-500">
              Texte de pied (PDF)
            </Label>
            <Textarea
              value={draft?.pied_libre || ""}
              onChange={(e) => setDraft((d) => ({ ...d, pied_libre: e.target.value }))}
              placeholder="Texte affiché en bas du devis / facture PDF…"
              rows={3}
              className="text-sm mt-1"
              data-testid="fae-pied-libre"
            />
          </div>
        </div>
        <div className="mt-4 pt-4 border-t border-gray-100">
          <IntervenantsEditor
            intervenants={draft?.intervenants || []}
            setIntervenants={(next) => setDraft((d) => ({ ...d, intervenants: next }))}
            mecaniciens={mecaniciens}
          />
          <div className="text-[10px] text-gray-400 mt-2">
            Utilisé pour les statistiques par mécanicien (page <em>Stats atelier</em>).
            Le CA HT est réparti au prorata des heures.
          </div>
        </div>
      </Card>

      <ArticlePickerDialog
        open={pickerOpen}
        onClose={() => { setPickerOpen(false); setEditingLigneIdx(null); }}
        onPick={(a) => {
          // iter165 — Support Article libre / MO forfait via helper unifié
          const presetLigne = buildLigneFromArticle(a, { tvaField: "tva_pct", tvaDefaut: 8.1 });
          setDraft((d) => {
            const lignes = d?.lignes || [];
            if (editingLigneIdx === null) {
              const _localId = (typeof crypto !== "undefined" && crypto.randomUUID)
                ? crypto.randomUUID()
                : `_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
              return { ...d, lignes: [...lignes, { _localId, ...presetLigne }] };
            }
            return { ...d, lignes: lignes.map((l, i) => i === editingLigneIdx ? { ...l, ...presetLigne } : l) };
          });
          setEditingLigneIdx(null);
        }}
      />

      <ExcelImportDialog
        open={excelImportOpen}
        onClose={() => setExcelImportOpen(false)}
        orId={orId}
        onImported={() => fetchOr()}
      />

      <ImportDevisDialog
        open={importDevisOpen}
        onClose={() => setImportDevisOpen(false)}
        orId={orId}
        clientId={or?.client_id}
        vehiculeId={or?.vehicule_id}
        currentLignesCount={(draft?.lignes || []).length}
        onImported={() => fetchOr()}
      />
    </div>
  );
}
